/**
 * Предустановка: Проект с feature веткой
 * Используется для уроков по ветвлению и слиянию
 */

import type { GitPreset } from './types';

/** Проект с feature веткой для демонстрации ветвления */
export const featureBranchPreset: GitPreset = {
  id: 'feature-branch',
  name: 'Проект с feature веткой',
  description: 'Проект с двумя ветками (main и feature) для изучения ветвления и слияния',

  currentBranch: 'main',

  branches: [
    { name: 'main', head: 'c4', color: '#F05032' },
    { name: 'feature/add-auth', head: 'c6', color: '#4a9eff' },
  ],

  commits: [
    {
      sha: 'c1',
      message: 'Initial commit: Project setup',
      parents: [],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704067200, // 2024-01-01
      files: ['README.md', 'package.json'],
    },
    {
      sha: 'c2',
      message: 'Add core modules',
      parents: ['c1'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704153600, // 2024-01-02
      files: ['src/index.ts', 'src/config.ts'],
    },
    {
      sha: 'c3',
      message: 'Implement user service',
      parents: ['c2'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704240000, // 2024-01-03
      files: ['src/user.ts', 'src/types.ts'],
    },
    {
      sha: 'c4',
      message: 'Add unit tests for user service',
      parents: ['c3'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704326400, // 2024-01-04
      files: ['tests/user.test.ts'],
    },
    // Feature branch commits
    {
      sha: 'c5',
      message: 'Start authentication module',
      parents: ['c2'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704240000, // 2024-01-03 (параллельно с c3)
      files: ['src/auth.ts'],
    },
    {
      sha: 'c6',
      message: 'Implement login and logout',
      parents: ['c5'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704326400, // 2024-01-04 (параллельно с c4)
      files: ['src/auth.ts', 'src/session.ts'],
    },
  ],

  files: [
    {
      path: 'README.md',
      content: `# User Management System

Система управления пользователями с аутентификацией.

## Установка

\`\`\`bash
npm install
\`\`\`

## Запуск

\`\`\`bash
npm start
\`\`\`

## Тестирование

\`\`\`bash
npm test
\`\`\`
`,
      status: 'committed',
    },
    {
      path: 'package.json',
      content: `{
  "name": "user-management",
  "version": "1.0.0",
  "description": "Система управления пользователями",
  "main": "src/index.ts",
  "scripts": {
    "start": "ts-node src/index.ts",
    "test": "jest"
  },
  "author": "Ivan Petrov",
  "license": "MIT"
}
`,
      status: 'committed',
    },
    {
      path: 'src/index.ts',
      content: `import { UserService } from './user';
import { config } from './config';

const userService = new UserService(config);

export { userService };
`,
      status: 'committed',
    },
    {
      path: 'src/config.ts',
      content: `export const config = {
  apiUrl: 'https://api.example.com',
  timeout: 5000,
  debug: false,
};
`,
      status: 'committed',
    },
    {
      path: 'src/user.ts',
      content: `import type { User, UserCreateInput } from './types';

export class UserService {
  private users: Map<string, User> = new Map();

  constructor(private config: { apiUrl: string; timeout: number }) {}

  async create(input: UserCreateInput): Promise<User> {
    const user: User = {
      id: crypto.randomUUID(),
      ...input,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    this.users.set(user.id, user);
    return user;
  }

  async findById(id: string): Promise<User | null> {
    return this.users.get(id) ?? null;
  }

  async findAll(): Promise<User[]> {
    return Array.from(this.users.values());
  }

  async delete(id: string): Promise<boolean> {
    return this.users.delete(id);
  }
}
`,
      status: 'committed',
    },
    {
      path: 'src/types.ts',
      content: `export interface User {
  id: string;
  email: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserCreateInput {
  email: string;
  name: string;
}

export interface AuthResult {
  success: boolean;
  token?: string;
  error?: string;
}
`,
      status: 'committed',
    },
    {
      path: 'tests/user.test.ts',
      content: `import { UserService } from '../src/user';

describe('UserService', () => {
  let service: UserService;

  beforeEach(() => {
    service = new UserService({ apiUrl: 'http://test', timeout: 1000 });
  });

  test('should create user', async () => {
    const user = await service.create({
      email: 'test@example.com',
      name: 'Test User',
    });

    expect(user.id).toBeDefined();
    expect(user.email).toBe('test@example.com');
  });

  test('should find user by id', async () => {
    const created = await service.create({
      email: 'find@example.com',
      name: 'Find Me',
    });

    const found = await service.findById(created.id);
    expect(found).toEqual(created);
  });

  test('should return null for non-existent user', async () => {
    const found = await service.findById('non-existent');
    expect(found).toBeNull();
  });
});
`,
      status: 'committed',
    },
    // Feature branch files
    {
      path: 'src/auth.ts',
      content: `import type { AuthResult } from './types';

export class AuthService {
  private tokens: Map<string, { userId: string; expiresAt: Date }> = new Map();

  async login(email: string, password: string): Promise<AuthResult> {
    // Симуляция аутентификации
    if (password.length < 6) {
      return { success: false, error: 'Password too short' };
    }

    const token = crypto.randomUUID();
    const expiresAt = new Date(Date.now() + 3600000); // 1 час

    this.tokens.set(token, { userId: email, expiresAt });

    return { success: true, token };
  }

  async logout(token: string): Promise<void> {
    this.tokens.delete(token);
  }

  async validateToken(token: string): Promise<boolean> {
    const data = this.tokens.get(token);
    if (!data) return false;

    return data.expiresAt > new Date();
  }
}
`,
      status: 'committed',
    },
    {
      path: 'src/session.ts',
      content: `import { AuthService } from './auth';

export interface Session {
  token: string;
  userId: string;
  startedAt: Date;
}

export class SessionManager {
  private sessions: Map<string, Session> = new Map();

  constructor(private auth: AuthService) {}

  async startSession(email: string, password: string): Promise<Session | null> {
    const result = await this.auth.login(email, password);

    if (!result.success || !result.token) {
      return null;
    }

    const session: Session = {
      token: result.token,
      userId: email,
      startedAt: new Date(),
    };

    this.sessions.set(result.token, session);
    return session;
  }

  async endSession(token: string): Promise<void> {
    await this.auth.logout(token);
    this.sessions.delete(token);
  }

  getActiveSession(token: string): Session | null {
    return this.sessions.get(token) ?? null;
  }
}
`,
      status: 'committed',
    },
  ],

  visualization: {
    branchColors: {
      main: '#F05032',
      'feature/add-auth': '#4a9eff',
    },
    nodePositions: {
      c1: { x: 100, y: 200 },
      c2: { x: 200, y: 200 },
      c3: { x: 300, y: 200 },
      c4: { x: 400, y: 200 },
      c5: { x: 300, y: 300 },
      c6: { x: 400, y: 300 },
    },
    annotations: [
      { sha: 'c2', text: 'Точка ветвления', position: 'bottom' },
      { sha: 'c6', text: 'Feature готова к слиянию', position: 'right' },
    ],
    highlightedCommits: ['c4', 'c6'],
  },
};
